import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Text, 
  VStack, 
  Heading,
  Grid,
  HStack,
  Button,
  Image  // Add Image import
} from '@chakra-ui/react';
import { Link, useSearchParams } from 'react-router-dom';
import { useArticles, useTags, useSettings } from '../hooks/useApi';
import { ArticlesPageSkeleton } from '../components/SkeletonComponents';

const Articles = () => {
  const [searchParams] = useSearchParams();
  const [selectedTag, setSelectedTag] = useState(searchParams.get('tag') || '');
  
  const { data: articles, loading: articlesLoading, error: articlesError } = useArticles();
  const { data: tags } = useTags();
  const { data: settings } = useSettings();

  // Filter articles by selected tag
  const filteredArticles = selectedTag 
    ? articles?.filter(article => 
        article.tags && article.tags.some(tag => 
          (tag.name || tag).toLowerCase() === selectedTag.toLowerCase()
        )
      )
    : articles;

  // Handle tag filtering
  const handleTagFilter = (tagName) => {
    if (selectedTag === tagName) {
      // If clicking the same tag, clear filter
      setSelectedTag('');
      // Clear URL params without causing navigation
      window.history.replaceState({}, '', '/articles');
    } else {
      // Set new tag filter
      setSelectedTag(tagName);
      // Update URL without causing navigation
      window.history.replaceState({}, '', `/articles?tag=${encodeURIComponent(tagName)}`);
    }
  };

  // Initialize selected tag from URL on mount
  useEffect(() => {
    const tagFromUrl = searchParams.get('tag') || '';
    if (tagFromUrl) {
      setSelectedTag(tagFromUrl);
    }
  }, [searchParams]); // Include searchParams dependency

  // Loading state
  if (articlesLoading) {
    return <ArticlesPageSkeleton />;
  }

  // Error state
  if (articlesError) {
    return (
      <Box py={{ base: 16, md: 20 }} px={4}>
        <VStack spacing={8} maxW="5xl" mx="auto">
          <Heading size={{ base: "xl", md: "2xl", lg: "4xl" }} textTransform="uppercase" letterSpacing="wide" textAlign="center">
            TECHNICAL ARTICLES
          </Heading>
          <Box bg="red.900" border="1px solid" borderColor="red.600" p={6} borderRadius="md" w="100%">
            <VStack spacing={3}>
              <Text color="red.300" fontSize={{ base: "md", md: "lg" }} fontWeight="bold">
                ⚠️ Error Loading Articles
              </Text>
              <Text color="white" textAlign="center">
                {articlesError}
              </Text>
              <Text color="gray.300" textAlign="center" fontSize={{ base: "xs", md: "sm" }}>
                Make sure your Django backend is running on localhost:8000
              </Text>
            </VStack>
          </Box>
        </VStack>
      </Box>
    );
  }

  // Helper function to format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Helper function to calculate read time (rough estimate)
  const calculateReadTime = (content) => {
    const wordsPerMinute = 200;
    const words = content ? content.split(' ').length : 0;
    const minutes = Math.ceil(words / wordsPerMinute);
    return Math.max(1, minutes); // Minimum 1 minute
  };

  // Get featured article (one marked as featured or most recent)
  const featuredArticle = filteredArticles && filteredArticles.length > 0 
    ? filteredArticles.find(article => article.is_featured) || filteredArticles[0] 
    : null;

  return (
    <Box py={{ base: 16, md: 20 }} px={4}>
      <VStack spacing={{ base: 16, md: 20 }} maxW="5xl" mx="auto">
        {/* Hero Section */}
        <VStack spacing={8} textAlign="center">
          <Heading size={{ base: "xl", md: "2xl", lg: "4xl" }} textTransform="uppercase" letterSpacing="wide">
            {settings?.articles_page_title || "TECHNICAL ARTICLES"}
          </Heading>
          <Box
            fontSize={{ base: "md", md: "lg", lg: "xl" }}
            color="gray.300"
            maxW="4xl"
            lineHeight="1.6"
            dangerouslySetInnerHTML={{
              __html: settings?.articles_page_description ||
                "Deep dives into engineering challenges, best practices, and lessons learned from building scalable web applications and cloud infrastructure."
            }}
            sx={{
              '& p': { mb: 4 },
              '& ul': { pl: 6, mb: 4 },
              '& ol': { pl: 6, mb: 4 },
              '& li': { mb: 2 },
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                color: 'white',
                fontWeight: 'bold',
                mb: 3,
                mt: 6
              },
              '& strong': { color: 'white' },
              '& a': { color: 'green.400', textDecoration: 'underline' }
            }}
          />
        </VStack>

        {/* Featured Article */}
        {featuredArticle && (
          <Box bg="gray.900" p={{ base: 6, md: 8, lg: 12 }} border="1px solid" borderColor="gray.700" w="100%">
            <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={{ base: 6, lg: 12 }} alignItems="center">
              <VStack align="start" spacing={6}>
                <VStack align="start" spacing={3}>
                  <HStack spacing={3}>
                    <Box w={2} h={2} bg="green.500" borderRadius="full" />
                    <Text color="green.500" fontSize={{ base: "xs", md: "sm" }} textTransform="uppercase" letterSpacing="wide">
                      FEATURED ARTICLE
                    </Text>
                  </HStack>
                  <Heading size={{ base: "lg", md: "xl", lg: "2xl" }} textTransform="uppercase" letterSpacing="wide">
                    {featuredArticle.title}
                  </Heading>
                  <Text color="gray.300" lineHeight="1.6" fontSize={{ base: "sm", md: "md" }}>
                    {featuredArticle.excerpt || 
                     (featuredArticle.content ? 
                      featuredArticle.content.substring(0, 200) + '...' : 
                      'No excerpt available'
                     )
                    }
                  </Text>
                  {featuredArticle.created_at && (
                    <Text color="gray.400" fontSize={{ base: "xs", md: "sm" }}>
                      Published {formatDate(featuredArticle.created_at)}
                    </Text>
                  )}
                </VStack>
                
                <HStack spacing={4}>
                  <Link to={`/articles/${featuredArticle.slug}`}>
                    <Button 
                      bg="green.500" 
                      color="white" 
                      size="sm"
                      _hover={{ bg: "green.600" }}
                    >
                      Read Article
                    </Button>
                  </Link>
                  <Text color="gray.400" fontSize={{ base: "xs", md: "sm" }}>
                    {featuredArticle.read_time} min read
                  </Text>
                </HStack>
              </VStack>

              {/* Featured Article Image */}
              <Box 
                h={{ base: "150px", md: "200px" }}
                bg="gray.800"
                border="1px solid"
                borderColor="gray.600"
                display="flex"
                alignItems="center"
                justifyContent="center"
                position="relative"
                overflow="hidden"
              >
                {featuredArticle.featured_image ? (
                  <Image
                    src={featuredArticle.featured_image}
                    alt={featuredArticle.title}
                    objectFit="cover"
                    w="100%"
                    h="100%"
                  />
                ) : (
                  /* Fallback design */
                  <>
                    <Text color="gray.400" fontSize={{ base: "xs", md: "sm" }} textAlign="center">
                      {featuredArticle.title}
                      <br />
                      Preview
                    </Text>
                    <Box
                      position="absolute"
                      top="20%"
                      right="20%"
                      w="30px"
                      h="30px"
                      border="1px solid"
                      borderColor="green.500"
                    />
                    <Box
                      position="absolute"
                      bottom="30%"
                      left="25%"
                      w="20px"
                      h="20px"
                      bg="green.500"
                      opacity={0.7}
                      borderRadius="full"
                    />
                  </>
                )}
              </Box>
            </Grid>
          </Box>
        )}


        {/* No Articles State */}
        {articles && articles.length === 0 && (
          <VStack spacing={8} textAlign="center" w="100%">
            <Box bg="gray.900" p={{ base: 8, md: 12 }} border="1px solid" borderColor="gray.700">
              <VStack spacing={4}>
                <Text fontSize={{ base: "3xl", md: "4xl" }}>📝</Text>
                <Heading size={{ base: "md", md: "lg" }} textTransform="uppercase" letterSpacing="wide">
                  NO ARTICLES YET
                </Heading>
                <Text color="gray.300" fontSize={{ base: "sm", md: "md" }}>
                  Articles will appear here once they're added to your Django CMS.
                </Text>
              </VStack>
            </Box>
          </VStack>
        )}

        {/* No Filtered Articles State */}
        {articles && articles.length > 0 && filteredArticles && filteredArticles.length === 0 && selectedTag && (
          <VStack spacing={8} textAlign="center" w="100%">
            <Box bg="gray.900" p={{ base: 8, md: 12 }} border="1px solid" borderColor="gray.700">
              <VStack spacing={4}>
                <Text fontSize={{ base: "3xl", md: "4xl" }}>🔍</Text>
                <Heading size={{ base: "md", md: "lg" }} textTransform="uppercase" letterSpacing="wide">
                  NO ARTICLES FOUND
                </Heading>
                <Text color="gray.300" fontSize={{ base: "sm", md: "md" }}>
                  No articles found with the tag "{selectedTag}". Try selecting a different category.
                </Text>
                <Button 
                  variant="outline"
                  borderColor="green.500"
                  color="green.500"
                  _hover={{ bg: "green.900" }}
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    handleTagFilter('');
                  }}
                >
                  Clear Filter
                </Button>
              </VStack>
            </Box>
          </VStack>
        )}

        {/* All Articles with Sidebar Filter */}
        {filteredArticles && filteredArticles.length > 0 && (
          <Box w="100%">
            <Heading size={{ base: "lg", md: "xl", lg: "3xl" }} textTransform="uppercase" letterSpacing="wide" textAlign="center" mb={{ base: 8, md: 12 }}>
              ALL ARTICLES
            </Heading>
            
            <VStack spacing={{ base: 6, md: 8 }} w="100%">
              {/* Mobile Category Filter - Horizontal */}
              {tags && tags.length > 0 && (
                <Box 
                  display={{ base: "block", md: "none" }}
                  w="100%"
                  bg="gray.900"
                  border="1px solid"
                  borderColor="gray.700"
                  p={4}
                >
                  <VStack spacing={4} align="start">
                    <Heading size="sm" textTransform="uppercase" letterSpacing="wide" color="white">
                      Browse by Category
                    </Heading>
                    
                    <HStack spacing={2} flexWrap="wrap" w="100%">
                      <Button 
                        variant="outline"
                        borderColor={!selectedTag ? "green.500" : "gray.600"}
                        color={!selectedTag ? "green.500" : "white"}
                        bg={!selectedTag ? "green.900" : "transparent"}
                        _hover={{ 
                          borderColor: "green.500", 
                          color: "green.500",
                          bg: !selectedTag ? "green.800" : "green.900"
                        }}
                        size="xs"
                        fontSize="xs"
                        onClick={(e) => {
                          e.preventDefault();
                          handleTagFilter('');
                        }}
                      >
                        All Articles
                      </Button>
                      
                      {tags.map((tag) => (
                        <Button 
                          key={tag.id}
                          variant="outline"
                          borderColor={selectedTag === tag.name ? "green.500" : "gray.600"}
                          color={selectedTag === tag.name ? "green.500" : "white"}
                          bg={selectedTag === tag.name ? "green.900" : "transparent"}
                          _hover={{ 
                            borderColor: "green.500", 
                            color: "green.500",
                            bg: selectedTag === tag.name ? "green.800" : "green.900"
                          }}
                          size="xs"
                          fontSize="xs"
                          onClick={(e) => {
                            e.preventDefault();
                            handleTagFilter(tag.name);
                          }}
                        >
                          {tag.name}
                        </Button>
                      ))}
                    </HStack>
                  </VStack>
                </Box>
              )}
              
              {/* Desktop Layout */}
              <HStack 
                align="start" 
                spacing={{ base: 4, md: 8 }} 
                w="100%"
                display={{ base: "none", md: "flex" }}
              >
                {/* Left Sidebar - Categories Filter (Desktop Only) */}
                {tags && tags.length > 0 && (
                  <Box 
                    w="250px"
                    flexShrink={0}
                    bg="gray.900"
                    border="1px solid"
                    borderColor="gray.700"
                    p={6}
                    h="fit-content"
                    position="sticky"
                    top={4}
                  >
                    <VStack spacing={4} align="start">
                      <Heading size="md" textTransform="uppercase" letterSpacing="wide" color="white">
                        Browse by Category
                      </Heading>
                      
                      <VStack spacing={2} align="stretch" w="100%">
                        <Button 
                          variant="outline"
                          borderColor={!selectedTag ? "green.500" : "gray.600"}
                          color={!selectedTag ? "green.500" : "white"}
                          bg={!selectedTag ? "green.900" : "transparent"}
                          _hover={{ 
                            borderColor: "green.500", 
                            color: "green.500",
                            bg: !selectedTag ? "green.800" : "green.900"
                          }}
                          size="sm"
                          fontSize="xs"
                          justifyContent="flex-start"
                          onClick={(e) => {
                            e.preventDefault();
                            handleTagFilter('');
                          }}
                        >
                          All Articles
                        </Button>
                        
                        {tags.map((tag) => (
                          <Button 
                            key={tag.id}
                            variant="outline"
                            borderColor={selectedTag === tag.name ? "green.500" : "gray.600"}
                            color={selectedTag === tag.name ? "green.500" : "white"}
                            bg={selectedTag === tag.name ? "green.900" : "transparent"}
                            _hover={{ 
                              borderColor: "green.500", 
                              color: "green.500",
                              bg: selectedTag === tag.name ? "green.800" : "green.900"
                            }}
                            size="sm"
                            fontSize="xs"
                            justifyContent="flex-start"
                            onClick={(e) => {
                              e.preventDefault();
                              handleTagFilter(tag.name);
                            }}
                          >
                            {tag.name}
                          </Button>
                        ))}
                      </VStack>
                    </VStack>
                  </Box>
                )}
                
                {/* Right Content - Articles List */}
                <VStack spacing={{ base: 6, md: 8 }} flex={1}>
                  <VStack spacing={4} w="100%">
                    {filteredArticles.map((article, index) => (
                <Box 
                  key={article.id} 
                  bg="gray.900" 
                  p={{ base: 4, md: 6 }} 
                  border="1px solid" 
                  borderColor="gray.700" 
                  w="100%"
                  _hover={{ bg: "gray.800" }}
                  transition="all 0.3s"
                >
                  <HStack justify="space-between" align="start" spacing={4} flexWrap={{ base: "wrap", md: "nowrap" }}>
                    {/* Left side - Article info */}
                    <VStack align="start" spacing={3} flex={1} minW={0}>
                      <Heading 
                        size={{ base: "md", md: "lg" }} 
                        textTransform="uppercase" 
                        letterSpacing="wide"
                        color="white"
                        noOfLines={2}
                      >
                        {article.title}
                      </Heading>
                      
                      {/* Tags */}
                      {article.tags && article.tags.length > 0 && (
                        <HStack spacing={2} flexWrap="wrap">
                          {article.tags.map((tag, tagIndex) => (
                            <HStack key={tagIndex} spacing={1}>
                              <Box w={1} h={1} bg="green.500" borderRadius="full" />
                              <Text 
                                color="green.500" 
                                fontSize={{ base: "xs", md: "sm" }} 
                                textTransform="uppercase" 
                                letterSpacing="wide"
                                cursor="pointer"
                                _hover={{ color: "green.400" }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleTagFilter(tag.name || tag);
                                }}
                              >
                                {tag.name || tag}
                              </Text>
                            </HStack>
                          ))}
                        </HStack>
                      )}
                      
                      {/* Article meta */}
                      <HStack spacing={4} color="gray.400" fontSize={{ base: "xs", md: "sm" }}>
                        {article.created_at && (
                          <Text>
                            {formatDate(article.created_at)}
                          </Text>
                        )}
                        <Text>
                          {article.read_time} min read
                        </Text>
                        {article.is_featured && (
                          <HStack spacing={1}>
                            <Box w={1.5} h={1.5} bg="green.500" borderRadius="full" />
                            <Text color="green.500" textTransform="uppercase" letterSpacing="wide">
                              Featured
                            </Text>
                          </HStack>
                        )}
                      </HStack>
                    </VStack>
                    
                    {/* Right side - Read button */}
                    <Box flexShrink={0}>
                      <Link to={`/articles/${article.slug}`}>
                        <Button 
                          bg="green.500" 
                          color="white" 
                          size="sm"
                          _hover={{ bg: "green.600" }}
                          fontSize={{ base: "xs", md: "sm" }}
                          px={{ base: 4, md: 6 }}
                        >
                          Read Article
                        </Button>
                      </Link>
                    </Box>
                  </HStack>
                </Box>
                    ))}
                  </VStack>
                  
                  {/* Article count */}
                  <Text color="gray.400" fontSize={{ base: "sm", md: "md" }} textAlign="center">
                    {selectedTag 
                      ? `Showing ${filteredArticles.length} article${filteredArticles.length !== 1 ? 's' : ''} tagged "${selectedTag}"`
                      : `${filteredArticles.length} total article${filteredArticles.length !== 1 ? 's' : ''}`
                    }
                  </Text>
                </VStack>
              </HStack>
              
              {/* Mobile Articles List */}
              <VStack 
                spacing={4} 
                w="100%"
                display={{ base: "flex", md: "none" }}
              >
                {filteredArticles.map((article) => (
                  <Box 
                    key={article.id} 
                    bg="gray.900" 
                    p={4} 
                    border="1px solid" 
                    borderColor="gray.700" 
                    w="100%"
                    _hover={{ bg: "gray.800" }}
                    transition="all 0.3s"
                  >
                    <VStack align="start" spacing={3} w="100%">
                      {/* Article Title */}
                      <Heading 
                        size="md"
                        textTransform="uppercase" 
                        letterSpacing="wide"
                        color="white"
                        noOfLines={2}
                      >
                        {article.title}
                      </Heading>
                      
                      {/* Tags */}
                      {article.tags && article.tags.length > 0 && (
                        <HStack spacing={2} flexWrap="wrap">
                          {article.tags.map((tag, tagIndex) => (
                            <HStack key={tagIndex} spacing={1}>
                              <Box w={1} h={1} bg="green.500" borderRadius="full" />
                              <Text 
                                color="green.500" 
                                fontSize="xs"
                                textTransform="uppercase" 
                                letterSpacing="wide"
                                cursor="pointer"
                                _hover={{ color: "green.400" }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleTagFilter(tag.name || tag);
                                }}
                              >
                                {tag.name || tag}
                              </Text>
                            </HStack>
                          ))}
                        </HStack>
                      )}
                      
                      {/* Article meta and button */}
                      <HStack justify="space-between" w="100%" align="center">
                        <VStack align="start" spacing={1}>
                          <HStack spacing={2} color="gray.400" fontSize="xs" flexWrap="wrap">
                            {article.created_at && (
                              <Text>
                                {formatDate(article.created_at)}
                              </Text>
                            )}
                            <Text>•</Text>
                            <Text>
                              {article.read_time} min read
                            </Text>
                            {article.is_featured && (
                              <>
                                <Text>•</Text>
                                <Text color="green.500" textTransform="uppercase" letterSpacing="wide">
                                  Featured
                                </Text>
                              </>
                            )}
                          </HStack>
                        </VStack>
                        
                        {/* Read button */}
                        <Box flexShrink={0}>
                          <Link to={`/articles/${article.slug}`}>
                            <Button 
                              bg="green.500" 
                              color="white" 
                              size="sm"
                              _hover={{ bg: "green.600" }}
                              fontSize="xs"
                              px={4}
                            >
                              Read Article
                            </Button>
                          </Link>
                        </Box>
                      </HStack>
                    </VStack>
                  </Box>
                ))}
              </VStack>
            </VStack>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default Articles;